import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridActionsCellItem, gridClasses } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckIcon from '@mui/icons-material/Check';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { alpha, styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { ruRU } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { logout } from './nuiCommon/nuiActions';
import axios from 'axios';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} disableInteractive classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: theme.typography.pxToRem(14),
    border: '1px solid #dadde9',
  },
}));

const theme = createTheme(
  ruRU
);

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      '&:hover': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity,
        ),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
}));

function formatIsActive(isActive) {
  return (isActive ? "✔" : "");
}

function dateFormat(date) {
  return date.format('DD.MM.YYYY');
}

const dateAsStringComparator = (v1, v2) =>
      dayjs(v1, "DD.MM.YYYY") - dayjs(v2, "DD.MM.YYYY");

export default function NuiAdminConsoleUsers() {
  const [rows, setRows] = React.useState([]);
  const [dateFrom, setDateFrom] = React.useState(dayjs(new Date()).subtract(1, 'year'));
  const [dateTo, setDateTo] = React.useState(dayjs(new Date()));
  const [search, setSearch] = React.useState("");
  const [inProgress, setInProgress] = React.useState(true);

  const [alertOpen, setAlertOpen] = React.useState(false);
  const handleAlertOpen = () => {
    setAlertOpen(true);
  };
  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const [alertText, setAlertText] = React.useState('');

  const handleAddBonusClick = (row) => () => {
    axios.get(`/api/user/addBonus?login=${row.login}`)
      .then((_) => {
        handleTableReload();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleRemoveBonusClick = (row) => () => {
    axios.get(`/api/user/addBonus?login=${row.login}&bonus=-1`)
      .then((_) => {
        handleTableReload();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleActivateUserManually = (row) => () => {
    axios.get(`/api/activateUserManually?login=${row.login}`)
      .then((response) => {
        setAlertText(response.data);
        handleAlertOpen();
        handleTableReload();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleCreateNewPasswordURL = (row) => () => {
    axios.get(`/api/createNewPasswordURL?login=${row.login}`)
      .then((response) => {
        setAlertText(response.data);
        handleAlertOpen();
        handleTableReload();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const columns = [
    { field: 'login', headerName: 'Логин', width: 180 },
    { field: 'firstName', headerName: 'Имя', width: 140 },
    { field: 'lastName', headerName: 'Фамилия', width: 140 },
    { field: 'middleName', headerName: 'Отчество', width: 140 },
    { field: 'email', headerName: 'Почта', width: 220 },
    {
      field: 'registerDate', headerName: 'Дата регистрации', width: 180,
      sortComparator: dateAsStringComparator
    },
    {
      field: 'isActive', headerName: 'Активирован', width: 120,
      valueGetter: (value, _) => formatIsActive(value)
    },
    { field: 'language', headerName: 'Язык', width: 80 },
    { field: 'serviceName', headerName: 'Сервис', width: 140 },
    { field: 'residenceCountry', headerName: 'Страна', width: 120 },
    {
      field: 'balanceList', headerName: 'Денег', width: 120,
      valueGetter: (value, _) => value.map(
        b => b.amount + " " + b.currencySymbol + "\n")
    },
    { field: 'bonus', headerName: 'Бонусов', width: 100 },
    {
      field: 'actions', headerName: 'Действия', type: 'actions', width: "100%",
      getActions: ({ row }) => {
        return [
          <HtmlTooltip
            title="Добавить бонус">
            <GridActionsCellItem
              icon={<AddIcon />}
              label="Добавить бонус"
              className="textPrimary"
              onClick={handleAddBonusClick(row)}
              color="inherit" />
          </HtmlTooltip>,
          <HtmlTooltip
            title="Удалить бонус">
            <GridActionsCellItem
              icon={<RemoveIcon />}
              label="Удалить бонус"
              onClick={handleRemoveBonusClick(row)}
              color="inherit" />
          </HtmlTooltip>,
          <HtmlTooltip
            title="Активировать учетную запись пользователя">
            <GridActionsCellItem
              icon={<CheckIcon />}
              label="Активировать"
              onClick={handleActivateUserManually(row)}
              color="inherit" />
          </HtmlTooltip>,
          <HtmlTooltip
            title="Новый пароль">
            <GridActionsCellItem
              icon={<LockOpenIcon />}
              label="Новый пароль"
              onClick={handleCreateNewPasswordURL(row)}
              color="inherit" />
          </HtmlTooltip>,
        ];
      }
    }
  ];

  async function handleTableReload() {
    setInProgress(true);
    await axios.get('/api/users?search=' + search +
                    '&dateFrom=' + dateFormat(dateFrom) +
                    '&dateTo=' + dateFormat(dateTo))
      .then((response) => {
        setRows(response.data);
        setInProgress(false);
      })
      .catch((error) => {
        setRows([]);
        console.log(error);
        setInProgress(false);
      });
  }

  React.useEffect(() => {handleTableReload();}, []);
  React.useEffect(() => {handleTableReload();}, [search]);

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
        <Box sx={{ height: '100%', width: '100%' }}>
          <Stack spacing={2} direction="row"
            sx={{ pb: 2 }}>
            <DatePicker label="Дата с"
              value={dateFrom}
              onChange={(newValue) => setDateFrom(newValue)}
              slotProps={{ textField: { variant: "standard" } }} />
            <DatePicker label="Дата по"
              value={dateTo}
              onChange={(newValue) => setDateTo(newValue)}
              slotProps={{ textField: { variant: "standard" } }} />
            <Button variant="contained" sx={{ height: '100%' }}
              onClick={handleTableReload}>
              Обновить
            </Button>
            <TextField label="Поиск" variant="standard" sx={{ width: '100%' }}
                       value={search}
                       onChange={(event) => setSearch(event.target.value)}/>
          </Stack>
          <StripedDataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 20,
                },
              },
              sorting: {
                sortModel: [{ field: 'registerDate', sort: 'desc' }],
              }
            }}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            pageSizeOptions={[20, 100]}
            isCellEditable={() => false}
            density="compact"
            loading={inProgress}
            slotProps={{
              loadingOverlay: {
                variant: 'linear-progress',
                noRowsVariant: 'linear-progress',
              },
            }}
          />
          <Button variant="contained"
                  sx={{mt: 2, float: 'right'}}
                  onClick={logout(() => setInProgress(true),
                                  () => setInProgress(false))}>
            Выйти
          </Button>
        </Box>

        <Dialog
          open={alertOpen}
          onClose={handleAlertClose} >
          <DialogContent>
            <DialogContentText sx={{"word-wrap": "break-word"}}>
              {alertText}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAlertClose} autoFocus>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
